import {
  AccountSettings,
  defaultAccountSettings,
  defaultNotificationsSettings,
  defaultUserInterfaceSettings,
  GridColumnSetting,
  NotificationsSettings,
  SelectedAccountOrClient,
  UserInterfaceSettings,
  UserSettingGetAndMigrate,
  WatchListGroup,
} from '@eih-netbroker/common';
import { ContainerSizeMap } from '../../../modules/layout/types/container';
import { ContainerStateMap } from '../../../modules/layout/types/containerState';
import { LayoutId } from '../../../modules/layout/types/layoutState';
import { SelectedWidgetMap } from '../../../modules/layout/types/selectedWidget';
import { WidgetType } from '../../../modules/layout/types/widgetType';
import { XLayout, XLayoutDisplay, XLayoutItem } from '../../../modules/layout/types/xlayout';
import { GridColumnWidthSetting } from '../../types/gridColumnWidthSetting';

export enum UserSetting {
  Account = 'Account',
  ChartDrawing = 'ChartDrawing',
  ChartLayout = 'ChartLayout',
  Notifications = 'Notifications',
  UserInterface = 'UserInterface',
  WatchList = 'WatchList',
  WidgetContainerState = 'WidgetContainerState',
  GridColumns = 'GridColumns',
  GridColumnsWidth = 'GridColumnsWidth',
  WatchListState = 'WatchListState',
  MarketTipChecked = 'MarketTipChecked',
  LastSelectedAccount = 'LastSelectedAccount',
  Layout2 = 'Layout2',
  LayoutDisplay = 'LayoutDisplay',
  ContainerSizes = 'ContainerSizes',
  SelectedWidgets = 'SelectedWidgets',
  FavoriteLayouts = 'FavoriteLayouts',
  FavoriteWidgets = 'FavoriteWidgets',
  UserMoneyVisible = 'UserMoneyVisible',
}

export const UserSettingWatchList: UserSettingGetAndMigrate<UserSetting.WatchList, Array<WatchListGroup>> = {
  key: UserSetting.WatchList,
  default: [],
  // example for migration
  // versions: [
  //   {
  //     version: 2,
  //     migrate: (from: unknown) => {
  //       if (Array.isArray(from)) {
  //         return from.map(item => {
  //           switch (item) {
  //             case '1':
  //               return mockedWatchListGroup1;
  //             case '2':
  //               return mockedWatchListGroup2;
  //             case '3':
  //               return mockedWatchListGroup3;
  //             case '4':
  //               return mockedWatchListGroup4;
  //             default:
  //               throw new Error();
  //           }
  //         });
  //       }
  //       throw new Error();
  //     },
  //   },
  //   {
  //     version: 3,
  //     migrate: (from: unknown) => {
  //       if (Array.isArray(from)) {
  //         return from.map(item => {
  //           switch ((item as WatchListGroup).id) {
  //             default:
  //               return mockedWatchListGroup2;
  //           }
  //         });
  //       }
  //       throw new Error();
  //     },
  //   },
  // ],
};

export const UserSettingsAccount: UserSettingGetAndMigrate<UserSetting.Account, AccountSettings> = {
  key: UserSetting.Account,
  default: defaultAccountSettings,
};

export const UserSettingsChartDrawing: UserSettingGetAndMigrate<UserSetting.ChartDrawing, object[]> = {
  key: UserSetting.ChartDrawing,
  default: [],
};

export const UserSettingsChartLayout: UserSettingGetAndMigrate<UserSetting.ChartLayout, object> = {
  key: UserSetting.ChartLayout,
  default: [],
};

export const UserSettingsWidgetContainer: UserSettingGetAndMigrate<
  UserSetting.WidgetContainerState,
  ContainerStateMap | undefined
> = {
  key: UserSetting.WidgetContainerState,
  default: undefined,
};

export const UserSettingsNotifications: UserSettingGetAndMigrate<UserSetting.Notifications, NotificationsSettings> = {
  key: UserSetting.Notifications,
  default: defaultNotificationsSettings,
};

export const UserSettingsUserInterface: UserSettingGetAndMigrate<UserSetting.UserInterface, UserInterfaceSettings> = {
  key: UserSetting.UserInterface,
  default: defaultUserInterfaceSettings,
};

/**
 * Generic column visibility settings for each grid around the application. For example what columns to display in the open positions widgets
 */
export const UserSettingsGridColumns: UserSettingGetAndMigrate<UserSetting.GridColumns, GridColumnSetting[]> = {
  key: UserSetting.GridColumns,
  default: [],
};

/**
 * Generic column visibility settings for each grid around the application. For example what columns to display in the open positions widgets
 */
export const UserSettingsGridColumnsWidth: UserSettingGetAndMigrate<
  UserSetting.GridColumnsWidth,
  GridColumnWidthSetting
> = {
  key: UserSetting.GridColumnsWidth,
  default: {},
};

export const UserSettingsWatchListState: UserSettingGetAndMigrate<UserSetting.WatchListState, number | undefined> = {
  key: UserSetting.WatchListState,
  default: undefined,
};

export const UserSettingsMarketTipState: UserSettingGetAndMigrate<UserSetting.MarketTipChecked, boolean | undefined> = {
  key: UserSetting.MarketTipChecked,
  default: false,
};

export const UserSettingsLastSelectedAccount: UserSettingGetAndMigrate<
  UserSetting.LastSelectedAccount,
  SelectedAccountOrClient | undefined
> = {
  key: UserSetting.LastSelectedAccount,
  default: undefined,
};

export const UserSettingsLayout2: UserSettingGetAndMigrate<UserSetting.Layout2, XLayout<XLayoutItem> | undefined> = {
  key: UserSetting.Layout2,
  default: undefined,
};

export const UserSettingsLayoutDisplay: UserSettingGetAndMigrate<
  UserSetting.LayoutDisplay,
  XLayoutDisplay | undefined
> = {
  key: UserSetting.LayoutDisplay,
  default: undefined,
};

export const UserSettingsContainerSize: UserSettingGetAndMigrate<
  UserSetting.ContainerSizes,
  ContainerSizeMap | undefined
> = {
  key: UserSetting.ContainerSizes,
  default: undefined,
};

export const UserSettingsSelectedWidgets: UserSettingGetAndMigrate<
  UserSetting.SelectedWidgets,
  SelectedWidgetMap | undefined
> = {
  key: UserSetting.SelectedWidgets,
  default: undefined,
};

export const UserSettingsFavoriteLayouts: UserSettingGetAndMigrate<UserSetting.FavoriteLayouts, Array<LayoutId>> = {
  key: UserSetting.FavoriteLayouts,
  default: [],
};

export const UserSettingsFavoriteWidgets: UserSettingGetAndMigrate<UserSetting.FavoriteWidgets, Array<WidgetType>> = {
  key: UserSetting.FavoriteWidgets,
  default: [],
};

export const UserSettingsUserMoneyVisible: UserSettingGetAndMigrate<UserSetting.UserMoneyVisible, boolean | undefined> =
  {
    key: UserSetting.UserMoneyVisible,
    default: true,
  };
